<template>
  <div class="bg-white rounded-2xl text-slate-600 shadow-sm">
    <div
      class="p-4 lg:p-6 border-b border-gray-100 flex flex-col md:flex-row gap-3 justify-between items-center bg-gray-50/50 rounded-t-2xl"
    >
      <div
        class="flex items-center gap-2 text-[13px] font-bold uppercase me-auto"
      >
        <span
          :class="[
            'w-2.5 h-2.5 rounded-full',
            isConnecting
              ? 'bg-amber-400 animate-pulse'
              : isDisconnected
              ? 'bg-red-500'
              : 'bg-emerald-500',
          ]"
        ></span>
        {{
          isConnecting ? "Connecting" : isDisconnected ? "Offline" : "Live Feed"
        }}
      </div>

      <div
        v-if="!selectedGame"
        class="relative w-full md:w-80 group"
        v-click-outside="closeDropdown"
      >
        <label
          class="absolute -top-2 left-3 bg-white px-1 text-[10px] text-slate-700 font-bold uppercase z-20"
        >
          Filter Game
        </label>

        <div
          @click="isDropdownOpen = !isDropdownOpen"
          class="relative w-full bg-white text-slate-700 border border-gray-200 rounded-lg pl-3 pr-10 py-2.5 text-sm cursor-pointer font-semibold flex items-center gap-3 hover:border-blue-400 hover:ring-4 hover:ring-blue-50 transition-all select-none z-10"
          :class="{ 'border-blue-500 ring-4 ring-blue-50': isDropdownOpen }"
        >
          <div
            class="w-14 h-8 rounded overflow-hidden border border-gray-100 bg-slate-50 flex-shrink-0"
          >
            <img
              v-if="selectedGameId !== 'allgames'"
              :src="getThumb(selectedGameId)"
              class="w-full h-full object-cover"
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center bg-slate-200 text-[8px] text-slate-400"
            >
              ALL
            </div>
          </div>
          <span class="truncate uppercase tracking-tight">
            {{
              selectedGameId === "allgames"
                ? "All Games"
                : getName(selectedGameId)
            }}
          </span>
          <div
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform duration-200"
            :class="{ 'rotate-180': isDropdownOpen }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <div
            v-if="isDropdownOpen"
            class="absolute z-50 mt-2 w-full bg-white border border-gray-200 shadow-2xl rounded-xl max-h-80 overflow-hidden flex flex-col p-1.5"
          >
            <div class="px-2 py-2 mb-1 border-b border-gray-50">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search game..."
                class="w-full px-3 py-2 text-sm border border-gray-200 !bg-transparent rounded-lg focus:outline-none focus:!border-blue-400 bg-gray-50/50"
                @click.stop
              />
            </div>
            <div class="overflow-y-auto flex-grow custom-scrollbar">
              <div
                v-if="!searchQuery"
                @click="selectGame('allgames')"
                class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all border-l-4 mb-1"
                :class="
                  selectedGameId === 'allgames'
                    ? 'bg-blue-50 border-[#386F99] text-[#386F99] shadow-sm'
                    : 'border-transparent hover:bg-slate-50 text-slate-600'
                "
              >
                <div
                  class="w-8 h-5 rounded flex items-center justify-center text-[8px] font-black border bg-slate-200 border-slate-300 text-slate-500"
                >
                  ALL
                </div>
                <span class="uppercase tracking-tight font-bold"
                  >All Games</span
                >
              </div>
              <div
                v-for="game in filteredGames"
                :key="game._id"
                @click="selectGame(game.game_id)"
                class="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm cursor-pointer transition-all mb-0.5 border-l-4"
                :class="
                  selectedGameId === game.game_id
                    ? 'bg-blue-50 border-[#386F99] text-[#386F99] shadow-sm'
                    : 'border-transparent hover:bg-slate-50 text-slate-600'
                "
              >
                <img
                  :src="`https://storage.googleapis.com/camasy-images/${game.thumb}`"
                  class="w-8 h-5 object-cover rounded shadow-sm border border-gray-100"
                />
                <span class="truncate block font-medium">{{ game.name }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <div
      class="relative min-h-[200px] md:h-[350px] flex flex-col rounded-b-2xl overflow-hidden"
    >
      <div
        v-if="isConnecting && allBets.length === 0"
        class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white"
      >
        <div
          class="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mb-3"
        ></div>
        <p class="text-slate-500 font-semibold animate-pulse text-xs">
          Connecting to live feed...
        </p>
      </div>

      <div
        v-show="!(isConnecting && allBets.length === 0)"
        class="flex-grow overflow-auto scrollbar-thin relative"
        @mouseenter="isHovered = true"
        @mouseleave="onMouseLeave"
      >
        <table
          v-if="allBets.length > 0"
          class="w-full text-left border-collapse text-[13px] min-w-[700px]"
        >
          <thead class="sticky top-0 z-10 bg-gray-50">
            <tr
              class="text-black font-semibold uppercase text-xs border-b border-gray-100"
            >
              <th
                v-if="!selectedGame"
                class="p-3 bg-gray-50/90 backdrop-blur-sm"
              >
                Game
              </th>
              <th class="p-3 bg-gray-50/90 backdrop-blur-sm">User</th>
              <th class="p-3 bg-gray-50/90 backdrop-blur-sm">Skin</th>
              <th class="p-3 bg-gray-50/90 backdrop-blur-sm">Action</th>
              <th class="p-3 bg-gray-50/90 backdrop-blur-sm">Amount</th>
              <th class="p-3 text-right bg-gray-50/90 backdrop-blur-sm">
                Time
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 bg-white">
            <tr
              v-for="(bet, index) in allBets"
              :key="bet.uuid"
              @click="goToHistory(bet)"
              class="hover:bg-blue-50/40 transition-colors group cursor-pointer"
              :class="[
                `type_${bet.type}`,
                {
                  'bg-emerald-50 border-emerald-100': bet.type === 'WIN',
                  'bg-slate-100/70 border-slate-200':
                    bet.type === 'BET' && bet.amount === 0,
                  'bet-row-enter': bet._isNew,
                },
              ]"
            >
              <td v-if="!selectedGame" class="p-2 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <div
                    class="w-10 h-8 rounded shadow-sm bg-slate-100 overflow-hidden border border-gray-200"
                  >
                    <img
                      :src="bet.thumb || fallbackThumb(bet.gameId)"
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    class="text-slate-700 font-bold uppercase text-[11px]"
                    >{{ bet.gameId }}</span
                  >
                </div>
              </td>
              <td class="p-2 truncate text-slate-500">{{ bet.userId }}</td>
              <td class="p-2 text-slate-500">{{ bet.skin_name }}</td>
              <td class="p-2">
                <span
                  :class="[
                    'font-semibold text-[9px] px-1.5 py-0.5 rounded-full border',
                    bet._badgeClasses,
                  ]"
                >
                  {{ bet._badgeText }}
                </span>
              </td>
              <td class="p-2 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="font-bold text-slate-800 text-[12px]"
                    >{{ bet.amountFormatted }}
                    <small>{{ bet.currency }}</small></span
                  >
                  <span class="text-[10px] text-slate-400"
                    >≈ {{ bet.amountConvertedFormatted }} EUR</span
                  >
                </div>
              </td>
              <td
                class="p-2 text-right text-slate-800 text-[12px] whitespace-nowrap"
              >
                {{ bet._relativeTime }}
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-else-if="!isConnecting"
          class="h-full flex flex-col items-center justify-center py-20"
        >
          <div class="bg-slate-50 rounded-full p-4 mb-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-8 w-8 text-slate-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <span class="text-slate-400 text-sm font-semibold"
            >No live bets yet!</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from "vue";
import socketIO from "socket.io-client";
import msgpack from "socket.io-msgpack-parser";
import { getFetch } from "@/api/axiosClient";

const MAX_BETS = 15;
// Relative-time labels are refreshed every 30s (was 10s) — still perfectly
// readable and halves the timer-driven re-renders.
const TICKER_INTERVAL_MS = 30_000;
// Throttle UI updates to at most once per second so the browser never freezes
// under high-frequency bet streams.
const FLUSH_INTERVAL_MS = 1_000;
const MAX_QUEUE_SIZE = 40;

export default {
  name: "LiveRoundInformation",
  props: {
    selectedGame: {
      type: Object,
      required: false,
      default: null,
    },
  },
  directives: {
    "click-outside": {
      beforeMount(el, binding) {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },

  data() {
    return {
      mainSocket: null,
      isConnecting: true,
      isDisconnected: false,
      isDropdownOpen: false,
      searchQuery: "",
      selectedGameId: "allgames",
      allBets: [],
      games: [],
      loading: false,
      ticker: null,
      now: Date.now(),
      isHovered: false,
    };
  },

  computed: {
    filteredGames() {
      if (!this.searchQuery) return this.games;
      const q = this.searchQuery.toLowerCase();
      return this.games.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.game_id.toLowerCase().includes(q),
      );
    },

    /**
     * Pre-computed gameId → thumb URL map so that the template never calls
     * Array.find() per row per render. Recomputed only when this.games changes.
     */
    thumbMap() {
      const map = {};
      for (const g of this.games) {
        map[
          g.game_id
        ] = `https://storage.googleapis.com/camasy-images/${g.thumb}`;
      }
      if (this.selectedGame) {
        map[
          this.selectedGame.game_id
        ] = `https://storage.googleapis.com/camasy-images/${this.selectedGame.game_thumb}`;
      }
      return map;
    },
  },

  watch: {
    selectedGame: {
      immediate: true,
      handler(newVal, oldVal) {
        const newId = newVal?.game_id || "allgames";
        const oldId = oldVal?.game_id || "allgames";

        if (newId === oldId && this.mainSocket) return;

        this.selectedGameId = newId;
        this.handleGameChange();
      },
    },

    /**
     * Refresh relative-time labels whenever `now` ticks.
     * We update bet._relativeTime in-place so only the text nodes change,
     * not the whole row.
     */
    now(ts) {
      for (const bet of this.allBets) {
        bet._relativeTime = this._formatRelativeTime(bet.timestamp, ts);
      }
    },
  },

  created() {
    // Keep incoming socket queue and batch timers non-reactive to avoid Vue reactivity overhead
    this._incomingQueue = [];
    this._flushTimer = null;
    this._initialFlushTimer = null;
  },

  mounted() {
    this.fetchGames();
    this.ticker = setInterval(() => {
      this.now = Date.now();
    }, TICKER_INTERVAL_MS);
    this._flushTimer = setInterval(() => {
      this._flushQueue();
    }, FLUSH_INTERVAL_MS);
  },

  beforeUnmount() {
    this.cleanupSocket();
    if (this._flushTimer) {
      clearInterval(this._flushTimer);
      this._flushTimer = null;
    }
    if (this._initialFlushTimer) {
      clearTimeout(this._initialFlushTimer);
      this._initialFlushTimer = null;
    }
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
    this._incomingQueue = [];
  },

  methods: {
    selectGame(gameId) {
      if (this.selectedGame) return;
      this.selectedGameId = gameId;
      this.isDropdownOpen = false;
      this.searchQuery = "";
      this.handleGameChange();
    },

    closeDropdown() {
      this.isDropdownOpen = false;
      this.searchQuery = "";
    },

    onMouseLeave() {
      this.isHovered = false;
      this._flushQueue();
    },

    handleGameChange() {
      this.isConnecting = true;
      this.allBets = [];
      this._incomingQueue = [];
      if (this._initialFlushTimer) {
        clearTimeout(this._initialFlushTimer);
        this._initialFlushTimer = null;
      }
      this.cleanupSocket();
      this.initializeSocket();
    },

    async fetchGames() {
      this.loading = true;
      try {
        const res = await getFetch(`/games`, {}, true, true);
        this.games = Array.isArray(res) ? res : res?.data || [];
      } catch (e) {
        console.error("Fetch games error", e);
        this.games = [];
      } finally {
        this.loading = false;
      }
    },

    initializeSocket() {
      const baseUrl =
        process.env.VUE_APP_URL_WSS || "https://stage-wss.theearplatform.com";
      // const skin_id =
      //   (this.$route?.params?.skinFilt && this.$route?.params?.skinFilt !== "all"
      //     ? parseInt(this.$route.params.skinFilt)
      //     : (this.$store?.getters?.skinStorage
      //         ? parseInt(this.$store.getters.skinStorage)
      //         : 4003));

      const skin_id = 4003;

      let newPath = process.env.VUE_APP_PATH_WSS || "wsstheearcasino";

      const socketInstance = socketIO(baseUrl, {
        path: "/" + newPath,
        parser: msgpack,
        transports: ["websocket"],
        reconnectionAttempts: 5,
        auth: {
          username: "crashgames",
          password: "crashgames",
          userId: Math.random().toString(36).substr(2, 9) + "_backoffice",
          skinId: skin_id,
          gameId: this.selectedGameId,
        },
      });

      this.mainSocket = markRaw(socketInstance);

      this.mainSocket.on("connect", () => {
        this.isConnecting = false;
        this.isDisconnected = false;
      });

      this.mainSocket.on("sendFeed", (data) => {
        this._enqueueFeedData(data);
      });

      this.mainSocket.on("disconnect", () => {
        this.isDisconnected = true;
        this.isConnecting = false;
      });

      this.mainSocket.on("connect_error", () => {
        this.isDisconnected = true;
        this.isConnecting = false;
      });
    },

    cleanupSocket() {
      if (this.mainSocket) {
        this.mainSocket.removeAllListeners();
        this.mainSocket.disconnect();
        this.mainSocket = null;
      }
    },

    // ─── Feed processing ────────────────────────────────────────────────────

    /**
     * Enqueue a raw socket event in a non-reactive array.
     * Raw events are buffered with O(1) overhead and flushed in controlled batches
     * via _flushQueue() every FLUSH_INTERVAL_MS (1000ms), eliminating main-thread
     * saturation and keeping page navigation instantaneous.
     */
    _enqueueFeedData(data) {
      if (!data) return;

      // Keep raw queue bounded to prevent memory growth during traffic bursts
      if (this._incomingQueue.length >= MAX_QUEUE_SIZE) {
        this._incomingQueue.shift();
      }
      this._incomingQueue.push(data);

      // If the feed is currently empty, flush quickly so the table doesn't sit empty
      if (this.allBets.length === 0 && !this._initialFlushTimer) {
        this._initialFlushTimer = setTimeout(() => {
          this._flushQueue();
          this._initialFlushTimer = null;
        }, 80);
      }
    },

    /**
     * Flushes a batch of buffered bets to allBets at a controlled rate (e.g. 1s).
     * This batches multiple incoming bets into a single Vue re-render cycle.
     */
    _flushQueue() {
      if (
        this.isHovered ||
        !this._incomingQueue ||
        !this._incomingQueue.length
      ) {
        return;
      }

      // If queue grew very large during a sudden burst, keep the latest 20 items
      // so the feed never lags behind real-time.
      if (this._incomingQueue.length > 20) {
        this._incomingQueue = this._incomingQueue.slice(-20);
      }

      // Dequeue 1-2 items (or up to 3 under sustained high volume)
      const count = Math.min(
        this._incomingQueue.length,
        this._incomingQueue.length > 6 ? 3 : 2
      );
      const batchRaw = this._incomingQueue.splice(0, count);

      // Build formatted entries ONLY for items that will actually be displayed
      const newEntries = batchRaw.map((raw) => this._buildEntry(raw));

      // Mark older items as not new so only newly entered rows animate
      for (let i = 0; i < this.allBets.length; i++) {
        this.allBets[i]._isNew = false;
      }

      // Reverse so newest bet is placed at index 0
      newEntries.reverse();

      // Prepend atomically and cap the list
      this.allBets.unshift(...newEntries);
      if (this.allBets.length > MAX_BETS) {
        this.allBets.length = MAX_BETS;
      }
    },

    /**
     * Build a display-ready entry object. All derived display strings and
     * thumbnails are pre-computed here so the template never runs logic per-cell.
     */
    _buildEntry(data) {
      const isWin = data.win !== 0 || data.win_converted !== 0;
      const amount = isWin ? data.win || 0 : data.amount || 0;
      const amountConverted = isWin
        ? data.win_converted || 0
        : data.amount_converted || 0;
      const type = isWin ? "WIN" : "BET";
      const isFreebet = type === "BET" && amount === 0;
      const ts = Date.now();
      const uniqueId =
        data.uuid ||
        `${data.roundId || "rnd"}_${data.user_id || "usr"}_${ts}_${Math.random()
          .toString(36)
          .substring(2, 7)}`;

      return {
        uuid: uniqueId,
        userId: data.user_id,
        gameId: data.game_id,
        amount,
        amountFormatted: Number(amount || 0).toFixed(2),
        amountConverted,
        amountConvertedFormatted: Number(amountConverted || 0).toFixed(2),
        currency: data?.currency_code ?? "",
        type,
        timestamp: ts,
        skin_name: data.skin_name ?? "N/A",
        round_id: data.roundId,
        token: data.token,
        provider_id: 207,
        thumb: this.getThumb(data.game_id),
        _isNew: true,
        // Pre-computed display helpers — updated in-place by the `now` watcher.
        _badgeText: isFreebet ? "FREESPINS / FREEBET" : type,
        _badgeClasses: isWin
          ? "text-emerald-600 bg-emerald-50 border-emerald-100"
          : isFreebet
          ? "text-indigo-600 bg-indigo-50 border-indigo-100"
          : "text-slate-400 bg-gray-50 border-gray-200",
        _relativeTime: this._formatRelativeTime(ts, this.now),
      };
    },

    // ─── Helpers ────────────────────────────────────────────────────────────

    getThumb(gameId) {
      return this.thumbMap[gameId] || this.fallbackThumb(gameId);
    },

    fallbackThumb(gameId) {
      return `https://storage.googleapis.com/camasy-images/casino/theearcasino/${gameId}.png`;
    },

    getName(gameId) {
      if (this.selectedGame && this.selectedGame.game_id === gameId) {
        return this.selectedGame.game_name;
      }
      if (!this.games.length) return gameId;
      const game = this.games.find((g) => g.game_id === gameId);
      return game ? game.name : gameId.replace(/-/g, " ");
    },

    _formatRelativeTime(ts, referenceTs) {
      const diffInSeconds = Math.floor((referenceTs - ts) / 1000);
      if (diffInSeconds < 5) return "Just now";
      if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours} hours ago`;
      return new Date(ts).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    goToHistory(bet) {
      const provider = process.env.VUE_APP_PROVIDER || "theearcasino";
      const providerId = process.env.VUE_APP_PROVIDER_ID || 207;
      if (!bet.round_id || !bet.gameId) return;
      const baseUrl =
        process.env.VUE_APP_HISTORY ||
        "https://stage-casino.theearplatform.com/stagemoregames/historyPage";
      const url = `${baseUrl}/?round_id=${bet.round_id}&game_id=${bet.gameId}&provider_slug=${provider ?? ""}&token=${bet.token || "demo"}&provider_id=${providerId}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
@keyframes betRowEnter {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bet-row-enter {
  animation: betRowEnter 0.35s ease-out both;
}
</style>
